import { __assign, __extends, __rest } from "tslib";
import * as React from 'react';
import { Animated, Easing, Platform, StyleSheet, View, Image } from 'react-native';
var DURATION = 2400;
/**
 * Activity indicator is used to present progress of some activity in the app.
 * It can be used as a drop-in for the ActivityIndicator shipped with React Native.
 *
 * <div class="screenshots">
 *   <img src="screenshots/activity-indicator.gif" style="width: 100px;" />
 * </div>
 *
 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { PnmacActivityIndicator, Colors } from 'pnmac-universal-components';
 *
 * const MyComponent = () => (
 *   <PnmacActivityIndicator animating={true} color={Colors.red800} />
 * );
 *
 * export default MyComponent;
 * ```
 */
var PnmacActivityIndicator = /** @class */function (_super) {
  __extends(PnmacActivityIndicator, _super);
  function PnmacActivityIndicator() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      timer: new Animated.Value(0),
      fade: new Animated.Value(!_this.props.animating && _this.props.hidesWhenStopped ? 0 : 1)
    };
    _this.startRotation = function () {
      var _a = _this.state,
        fade = _a.fade,
        timer = _a.timer;
      // Show indicator
      Animated.timing(fade, {
        duration: 200 * 1.0,
        toValue: 1,
        isInteraction: false,
        useNativeDriver: true
      }).start();
      // Circular animation in loop
      if (_this.rotation) {
        timer.setValue(0);
        Animated.loop(_this.rotation).start();
      }
    };
    _this.stopRotation = function () {
      if (_this.rotation) {
        _this.rotation.stop();
      }
    };
    return _this;
  }
  PnmacActivityIndicator.prototype.componentDidMount = function () {
    var animating = this.props.animating;
    var timer = this.state.timer;
    // Circular animation in loop
    this.rotation = Animated.timing(timer, {
      duration: DURATION,
      easing: Easing.linear,
      // Animated.loop does not work if useNativeDriver is true on web
      useNativeDriver: Platform.OS !== 'web',
      toValue: 1,
      isInteraction: false
    });
    if (animating) {
      this.startRotation();
    }
  };
  PnmacActivityIndicator.prototype.componentDidUpdate = function (prevProps) {
    var _a = this.props,
      animating = _a.animating,
      hidesWhenStopped = _a.hidesWhenStopped;
    var fade = this.state.fade;
    if (animating !== prevProps.animating) {
      if (animating) {
        this.startRotation();
      } else if (hidesWhenStopped) {
        // Hide indicator first and then stop rotation
        Animated.timing(fade, {
          duration: 200 * 1.0,
          toValue: 0,
          useNativeDriver: true,
          isInteraction: false
        }).start(this.stopRotation.bind(this));
      } else {
        this.stopRotation();
      }
    }
  };
  PnmacActivityIndicator.prototype.render = function () {
    var _a = this.state,
      fade = _a.fade,
      timer = _a.timer;
    var _b = this.props,
      animating = _b.animating,
      indicatorColor = _b.color,
      hidesWhenStopped = _b.hidesWhenStopped,
      indicatorSize = _b.size,
      style = _b.style,
      rest = __rest(_b, ["animating", "color", "hidesWhenStopped", "size", "style"]);
    var color = indicatorColor || "#003087";
    var size = typeof indicatorSize === 'string' ? indicatorSize === 'small' ? 48 : 96 : indicatorSize ? indicatorSize : 96;
    var frames = 60 * DURATION / 1000;
    var easing = Easing.bezier(0.4, 0.0, 0.7, 1.0);
    var containerStyle = {
      width: size,
      height: size / 2,
      overflow: 'hidden'
    };
    return /*#__PURE__*/React.createElement(View, __assign({
      style: [styles.container, style]
    }, rest), /*#__PURE__*/React.createElement(Animated.View, {
      style: [{
        width: size,
        height: size,
        opacity: fade
      }]
    }, [0, 1].map(function (index) {
      var inputRange = Array.from(new Array(frames), function (_, frameIndex) {
        return frameIndex / (frames - 1);
      });
      var outputRange = Array.from(new Array(frames), function (_, frameIndex) {
        var progress = 2 * frameIndex / (frames - 1);
        var rotation = index ? +(360 - 15) : -(180 - 15);
        if (progress > 1.0) {
          progress = 2.0 - progress;
        }
        var direction = index ? -1 : +1;
        return "".concat(direction * (180 - 30) * easing(progress) + rotation, "deg");
      });
      var layerStyle = {
        width: size,
        height: size,
        transform: [{
          rotate: timer.interpolate({
            inputRange: [0, 1],
            outputRange: ["".concat(0 + 30 + 15, "deg"), "".concat(2 * 360 + 30 + 15, "deg")]
          })
        }]
      };
      var viewportStyle = {
        width: size,
        height: size,
        transform: [{
          translateY: index ? -size / 2 : 0
        }, {
          rotate: timer.interpolate({
            inputRange: inputRange,
            outputRange: outputRange
          })
        }]
      };
      var offsetStyle = index ? {
        top: size / 2
      } : null;
      var lineStyle = {
        width: size,
        height: size,
        borderColor: color,
        borderWidth: size / 10,
        borderRadius: size / 2,
        borderStyle: 'dashed'
      };
      return /*#__PURE__*/React.createElement(Animated.View, {
        key: index,
        style: [styles.layer]
      }, /*#__PURE__*/React.createElement(Animated.View, {
        style: layerStyle
      }, /*#__PURE__*/React.createElement(Animated.View, {
        style: [containerStyle, offsetStyle],
        collapsable: false
      }, /*#__PURE__*/React.createElement(Animated.View, {
        style: viewportStyle
      }, /*#__PURE__*/React.createElement(Animated.View, {
        style: containerStyle,
        collapsable: false
      }, /*#__PURE__*/React.createElement(Animated.View, {
        style: lineStyle
      }))))));
    })));
  };
  PnmacActivityIndicator.defaultProps = {
    animating: true,
    size: 'small',
    hidesWhenStopped: true
  };
  return PnmacActivityIndicator;
}(React.Component);
var styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  layer: __assign(__assign({}, StyleSheet.absoluteFillObject), {
    justifyContent: 'center',
    alignItems: 'center'
  })
});
export default PnmacActivityIndicator;